import { Button, Card, CardContent, H5, H6 } from "@sellar-io/components";
import {
  DiscoveryProductsBestsellersListResponseType,
  DiscoveryProductsRecentlyAddedResponseType,
} from "@sellar-io/api-v2/types";
import { pluralize } from "@sellar-io/common";
import { InfiniteData } from "@tanstack/react-query";
import { useEffect, useEffectEvent, useState } from "react";
import { useNavigate } from "react-router";
import { twMerge } from "tailwind-merge";
import { useLocalStorage, useMediaQuery } from "usehooks-ts";
import { useShallow } from "zustand/react/shallow";
import { Icon, SpinnerLegacy } from "../../../components";
import {
  BestPricePromise,
  BestPricePromiseFullSize,
  BestPricePromiseCompact,
} from "../../../components/bestPricePromise/BestPricePromise";
import { BestPricePromiseDialog } from "../../../components/bestPricePromise/BestPricePromiseDialog";
import { ImageWithFallback } from "../../../components/image/imageWithFallback";
import { IntersectingInfiniteLoadingButton } from "../../../components/infiniteLoading/IntersectingInfiniteLoadingButton";
import { SellarMap } from "../../../components/map/SellarMap";
import { defaultLondonLocation } from "../../../components/map/SellarMap.shared";
import { useSellarMapStore } from "../../../components/map/SellarMapStore";
import { useMenuDrawerContext } from "../../../components/menu/MenuDrawerContextProvider.shared";
import {
  NavigationBar,
  NavigationBarSubHeader,
} from "../../../components/navigation/NavigationBar";
import { NavigationBarSearchOverlay } from "../../../components/navigation/NavigationBarSearchOverlay";
import { ValuePropsContainer } from "../../../components/valuePropsContainer/ValuePropsContainer";
import {
  DISCOVER_BEST_SELLERS,
  DISCOVER_PRE_ORDER,
  DISCOVER_RECENTLY_ADDED,
  DISCOVER_SEARCH,
  DISCOVER_SUPPLIER_MAP,
  DISCOVER_SUPPLIERS_DIRECT,
  DISCOVER_SUPPLIERS_RECENT,
  ORDER_BUYER,
} from "../../../constants/routes";
import { BuyerVenueTypeForm } from "../../../forms/buyer/BuyerVenueTypeForm";
import { useEvents, useSellarUser } from "../../../hooks";
import { useDiscoveryProductBestsellersList } from "../../../hooks/api/v2/discovery/useDiscoveryProductBestsellersList";
import { useDiscoveryProductRecentlyAdded } from "../../../hooks/api/v2/discovery/useDiscoveryProductRecentlyAdded";
import { useDiscoveryPreOrdersProducts } from "../../../hooks/api/v2/discovery/useDiscoveryPreOrders";
import { useDiscoverySupplierList } from "../../../hooks/api/v2/discovery/useDiscoverySupplierList";
import { useMarketSpotlightPageLayout } from "../../../hooks/api/v2/marketSpotlightLayout/useMarketSpotlightPageLayout";
import { useMatchedBuyersList } from "../../../hooks/api/v2/retailer/useMatchedBuyersList";
import { useSupplier } from "../../../hooks/api/v2/supplier/useSupplier";
import { useSupplierListByDistance } from "../../../hooks/api/v2/supplier/useSupplierListByDistance";
import { useSupplierRecentlyAddedNew } from "../../../hooks/api/v2/supplier/useSupplierRecentlyAddedNew";
import { useLocationFromPostcode } from "../../../hooks/api/v2/location/useLocationFromPostcode";
import { useDisplaySizeVariant } from "../../../hooks/useDisplaySizeVariant";
import { useFeatureFlagStore } from "../../../hooks/useFeatureFlags";
import { useMarketStorefrontSessionId } from "../../../hooks/useMarketStorefrontSessionTracking";
import { useNavigateToChild } from "../../../hooks/useParentNavigation";
import { useSearchParamsPostcodeCheck } from "../../../hooks/useSearchParamsPostcodeCheck";
import { useSellarCurrentBusiness } from "../../../hooks/useSellarCurrentBusiness.shared";
import { useSignupContext } from "../../../hooks/useSignupContext.shared";
import { createScrollRestorationState } from "../../../utils/scrollRestoration";
import { getSellarWebsiteUtms } from "../../../utils/utmHelper";
import { SpotlightBanner } from "../../spotlight/SpotlightBanner/SpotlightBanner";
import { SpotlightListCollectionProducts } from "../../spotlight/SpotlightListCollection/SpotlightListCollectionProducts";
import { SpotlightListCollectionRegional } from "../../spotlight/SpotlightListCollection/SpotlightListCollectionRegional";
import { StorefrontPostcodeDialogContainer } from "../../stockList/categoryPage/Storefront/StorefrontDialogs/StorefrontPostcodeDialog/StorefrontPostcodeDialogContainer";
import { DiscoveryHorizontalList } from "./components/DiscoveryHorizontalList";
import { DiscoveryMenuBar } from "./components/DiscoveryMenuBar";
import { DiscoveryProductDialog } from "./components/DiscoveryProductDialog";
import { DiscoverySuggestionTag } from "./components/DiscoverySuggestionTag";
import { DiscoverySupplierCardCompact } from "./components/DiscoverySupplierCardCompact";
import { DiscoveryListProductCard } from "./DiscoveryListProductCard";
import { DiscoveryListSupplierCard as DiscoveryListSupplierCardV2 } from "./DiscoveryListSupplierCard";
import { randomizeDiscoverySuggestionTags } from "./discoverySuggestionTags";
import {
  DiscoveryContainerCards,
  DiscoverySuggestionTagContainer,
} from "./DiscoveryV2";
import { DiscoveryMarketSearch } from "../components/DiscoveryMarketSearch";
import {
  DiscoveryV2RecentlyViewedStoreState,
  notRecentlyViewedExpired,
  setRecentlyViewedExpiry,
  useDiscoveryV2RecentlyViewedStore,
} from "./DiscoveryV2RecentlyViewedStore";
import { useDiscoveryV2SearchHistoryStore } from "./DiscoveryV2SearchHistoryStore";
import { useDiscoveryV2SearchStore } from "./DiscoveryV2SearchStore";
import {
  DiscoverPreOrderProductWithSupplierType,
  DiscoveryPreOrdersResponseType,
} from "@sellar-io/api-v2/types";
import { PreOrderPillToolTip } from "./components/DiscoveryPreOrderProductCard";
import { SellarCreditCheckBanner } from "../../sellarCredit/components/SellarCreditCheckBanner";
import { Video } from "../../../components/video/Video";
import { DiscoveryV2DistributionSuppliersBanner } from "./distribution/DiscoveryV2DistributionSuppliersBanner";
import { DiscoveryV2DistributionSuppliersBannerV2Container } from "./distribution/DiscoveryV2DistributionSuppliersBannerV2";
import { useDiscoveryV2DistributionTracking } from "./distribution/useDiscoveryV2DistributionTracking";
import { DraftBasketDrawer } from "./distribution/DraftBasketDrawer";
import { MarketBasketFeedbackProvider } from "./distribution/MarketBasketFeedbackContext";
import { MarketBasketFlyover } from "./distribution/MarketBasketFlyover";
import { useDraftDbsBaskets } from "./distribution/useDraftDbsBaskets";
import {
  ConsolidatedBasketOpenSource,
  useConsolidatedBasketTracking,
} from "./distribution/consolidatedOrderingTracking";
import { QuantityIndicator } from "../../../components/quantityIndicator/QuantityIndicator";
import { useRemoteFeatureFlags } from "../../../hooks/useRemoteFeatureFlags";
import { useCanShowDeliveredBySellarOptionV2 } from "./lib/useCanShowDeliveredBySellarOptionV2";

export const unAuthorisedUserPostcodeInLocalStorageKey =
  "unauthorisedUserPostcode";

export const DiscoveryV2Home = () => {
  const { onTrackSupplierClick, onTrackBannerClick } =
    useDiscoveryV2DistributionTracking();
  const { setIsOnScreen } = useMenuDrawerContext();
  const [unAuthorisedUserPostcode, setUnAuthorisedUserPostcode] =
    useLocalStorage<string | undefined>(
      unAuthorisedUserPostcodeInLocalStorageKey,
      undefined,
    );
  const [unAuthorisedUserLocation, setUnAuthorisedUserLocation] =
    useLocalStorage<{ lat: number; lng: number } | undefined>(
      "unAuthorisedUserLocation",
      undefined,
    );

  const [postcodeDialogActive, setPostcodeDialogActive] = useState(false);
  const isMobile = useMediaQuery("(max-width: 700px)");
  const [draftBasketDrawerActive, setDraftBasketDrawerActive] = useState(false);
  const { business } = useSellarCurrentBusiness();
  const { user } = useSellarUser();
  const autocompleteEnabled = useRemoteFeatureFlags().isActive(
    "marketSearchAutocomplete",
  );
  const sdConsolidatedCheckoutEnabled = useRemoteFeatureFlags().isActive(
    "sdConsolidatedCheckout",
  );
  const canShowDeliveredBySellar = useCanShowDeliveredBySellarOptionV2();
  const { totalItemCount, baskets } = useDraftDbsBaskets();
  const { trackBasketOpened } = useConsolidatedBasketTracking();
  const openBasket = (source: ConsolidatedBasketOpenSource) => {
    trackBasketOpened(baskets, source);
    setDraftBasketDrawerActive(true);
  };
  const [bestPricePromiseDialogActive, setBestPricePromiseDialogActive] =
    useState<boolean>(false);
  const [showVenueTypesForm] = useState<boolean>(
    !business?.Retailer?.venueTypes.length,
  );

  const { setSearchValue } = useDiscoveryV2SearchStore(
    useShallow((state) => ({
      setSearchValue: state.setSearchValue,
    })),
  );
  const { isActive } = useFeatureFlagStore();

  const bestsellersEnabled = isActive("DISCOVERY_BESTSELLERS_ENABLED");

  const buyerPostcode =
    business?.type === "retailer"
      ? business.DeliveryAddress.postalCode
      : undefined;

  const discoverySupplierListQuery = useDiscoverySupplierList({
    limit: 20,
    retailerPostcode: buyerPostcode,
    unAuthorisedUserPostcode,
  });

  const unauthenticatedUserLocationQuery = useLocationFromPostcode(
    unAuthorisedUserPostcode,
    user,
    unAuthorisedUserLocation,
  );

  useEffect(() => {
    if (
      unauthenticatedUserLocationQuery.data &&
      !unAuthorisedUserLocation &&
      !user
    ) {
      setUnAuthorisedUserLocation(unauthenticatedUserLocationQuery.data);
    }
  }, [
    unauthenticatedUserLocationQuery.data,
    unAuthorisedUserLocation,
    user,
    setUnAuthorisedUserLocation,
  ]);

  const discoveryRecentlyAddedQuery = useDiscoveryProductRecentlyAdded({
    limit: 20,
    retailerPostcode: buyerPostcode,
    unAuthorisedUserPostcode,
  });

  const preOrderQuery = useDiscoveryPreOrdersProducts({
    limit: 20,
    retailerPostcode: buyerPostcode,
    unAuthorisedUserPostcode,
  });

  const preOrdersExist = preOrderQuery.isSuccess;

  const postcodeParams = useSearchParamsPostcodeCheck();
  useEffect(() => {
    if (
      !business &&
      !unAuthorisedUserPostcode &&
      postcodeParams.postcode &&
      postcodeParams.isValid
    ) {
      setUnAuthorisedUserPostcode(postcodeParams.postcode);
    }
  }, [
    business,
    unAuthorisedUserPostcode,
    postcodeParams,
    setUnAuthorisedUserPostcode,
  ]);

  const displaySuppliersRecentlyAdded = discoveryRecentlyAddedQuery.isSuccess;

  const bestsellersProductQuery = useDiscoveryProductBestsellersList({
    limit: 20,
    retailerPostcode: buyerPostcode,
    unAuthorisedUserPostcode,
  });

  const bestsellersProducts =
    bestsellersProductQuery.data?.pages.flatMap((page) => page) ?? [];

  const displayBestsellers = !!bestsellersProducts.length && bestsellersEnabled;

  const navigate = useNavigate();
  const navigateToChild = useNavigateToChild();

  const { track } = useEvents();

  const [selectedProduct, setSelectedProduct] = useState<
    | DiscoveryProductsRecentlyAddedResponseType[number]
    | DiscoverPreOrderProductWithSupplierType
    | undefined
  >(undefined);

  const { sessionId, getSessionHistory } = useMarketStorefrontSessionId();
  const trackInitialSession = useEffectEvent(() => {
    if (sessionId && getSessionHistory().length === 1) {
      track("Discover Page Viewed", { ...getSellarWebsiteUtms(), sessionId });
    }
  });

  useEffect(() => {
    trackInitialSession();
  }, [sessionId]);

  const onProductClicked = (
    product:
      | DiscoveryProductsRecentlyAddedResponseType[number]
      | DiscoverPreOrderProductWithSupplierType,
    source: string,
  ) => {
    if (product.Supplier) {
      setSelectedProduct(product);
      useDiscoveryV2RecentlyViewedStore.getState().add({
        id: product.id,
        supplierId: product.Supplier.id,
        name: product.name ?? "",
        imageUrl: product.imageUrl,
        logoUrl: product.Supplier.Business?.logoUrl,
        urlId: product.Supplier.urlId,
        details: `${product.kind} // ${product.abv ?? ""}%`,
        expiry: setRecentlyViewedExpiry(),
        containsPreOrderProductItem: product.containsPreOrderProductItem,
      });
      track("Discover product clicked", {
        supplier: product.Supplier.urlId,
        product: product.name,
        source,
        containsPreOrderProductItem: product.containsPreOrderProductItem,
        sessionId,
        ...getSellarWebsiteUtms(),
      });
    }
  };

  const showUnauthUserPostcodeInNav = !user && unAuthorisedUserPostcode;

  const videoComponentEnabled = useFeatureFlagStore().isActive(
    "VIDEO_COMPONENT_SPIKE",
  );

  const bestPricePromise = (
    <BestPricePromise onClick={() => setBestPricePromiseDialogActive(true)}>
      {isMobile ? <BestPricePromiseCompact /> : <BestPricePromiseFullSize />}
    </BestPricePromise>
  );
  const renderMarketNavigationBar = ({
    menuActive,
    closeMenu,
  }: {
    menuActive: boolean;
    closeMenu: () => void;
  }) => (
    <NavigationBar
      leftIcon={
        showUnauthUserPostcodeInNav
          ? "MAP_MARKER"
          : menuActive
            ? "CHEVRON_LEFT"
            : "MENU"
      }
      leftIconText={
        showUnauthUserPostcodeInNav ? unAuthorisedUserPostcode : undefined
      }
      onClickLeftIcon={() => {
        if (showUnauthUserPostcodeInNav) {
          return setPostcodeDialogActive(true);
        }
        if (!menuActive) {
          return setIsOnScreen(true);
        }
        closeMenu();
      }}
      title={"Market"}
      borderBottom={false}
      leftComponent={!isMobile ? bestPricePromise : undefined}
      rightComponent={
        user ? (
          <MarketBasketFlyover onViewBasket={openBasket}>
            <div
              className="flex cursor-pointer items-center gap-1"
              onClick={() => openBasket("navbar")}
            >
              <Icon icon="CART_OUTLINE" size={1} />
              {canShowDeliveredBySellar && totalItemCount > 0 && (
                <QuantityIndicator
                  quantity={totalItemCount}
                  className="bg-[#A34FD2]"
                />
              )}
            </div>
          </MarketBasketFlyover>
        ) : isMobile ? (
          bestPricePromise
        ) : undefined
      }
    />
  );
  const navigateToSearch = (searchValue: string) => {
    setSearchValue(searchValue);
    if (searchValue) {
      navigate(DISCOVER_SEARCH, {
        state: createScrollRestorationState(),
      });
    }
  };

  return (
    <MarketBasketFeedbackProvider onViewBasket={openBasket}>
      {autocompleteEnabled ? (
        <>
          {renderMarketNavigationBar({
            menuActive: false,
            closeMenu: () => undefined,
          })}
          <NavigationBarSubHeader>
            <div className="m-auto w-full max-w-[1200px] p-4">
              <DiscoveryMarketSearch fullWidth onSubmit={navigateToSearch} />
            </div>
          </NavigationBarSubHeader>
        </>
      ) : (
        <NavigationBarSearchOverlay
          hasDashboardSidebar={Boolean(user)}
          navigationBar={({ open, close }) =>
            renderMarketNavigationBar({
              menuActive: open,
              closeMenu: close,
            })
          }
          searchInput={({
            autoFocus,
            inputRef,
            portalRoot,
            close,
            suggestionsEnabled,
          }) => (
            <DiscoveryMarketSearch
              fullWidth
              autoFocus={autoFocus}
              inputRef={inputRef}
              portalRoot={portalRoot}
              suggestionsEnabled={suggestionsEnabled}
              onCommit={close}
              onSubmit={navigateToSearch}
            />
          )}
        >
          {({ close }) => (
            <DiscoveryV2HomeMenu
              onChangeSearchValue={(searchValue) => {
                close();
                navigateToSearch(searchValue);
              }}
            />
          )}
        </NavigationBarSearchOverlay>
      )}

      {sdConsolidatedCheckoutEnabled ? (
        <DiscoveryV2DistributionSuppliersBannerV2Container
          supplierListVisibility="visible"
          contentClassName="p-4"
          unAuthorisedUserPostcode={unAuthorisedUserPostcode}
          onTrackSupplierClick={({ supplierName }) => {
            onTrackSupplierClick({
              supplierName,
              source: "market",
            });
          }}
          onTrackBannerClick={() => {
            onTrackBannerClick({
              source: "market",
            });
          }}
          onOpenDbsDraftBasketDrawer={() => setDraftBasketDrawerActive(true)}
        />
      ) : (
        <DiscoveryV2DistributionSuppliersBanner
          contentClassName="p-4"
          unAuthorisedUserPostcode={unAuthorisedUserPostcode}
          onTrackSupplierClick={({ supplierName }) => {
            onTrackSupplierClick({
              supplierName,
              source: "market",
            });
          }}
          onTrackBannerClick={() => {
            onTrackBannerClick({
              source: "market",
            });
          }}
        />
      )}
      <div
        className={
          "m-auto flex w-[100vw] max-w-300 flex-col gap-4 pt-4 transition-opacity min-[640px]:w-[88vw] min-[1280px]:w-[79vw]"
        }
      >
        <SellarCreditCheckBanner className="px-4" />

        <DiscoveryMenuBar />

        {!business && <MatchedBusinessValueProp />}

        {videoComponentEnabled && (
          <Video
            videoId="pGNRjIoM72w"
            className="mx-auto w-full max-w-2xl rounded-lg"
          />
        )}

        {/*SPOTLIGHT*/}
        <Spotlight />

        {/*SUPPLIERS DIRECT*/}
        <DiscoveryV2DirectSuppliers
          buyerPostcode={buyerPostcode}
          unAuthorisedUserPostcode={
            !user ? unAuthorisedUserPostcode : undefined
          }
          unAuthorisedUserLocation={
            !user ? unAuthorisedUserLocation : undefined
          }
        />

        {/*SUPPLIERS LOCAL MAP*/}
        <DiscoveryV2SuppliersLocal />

        {/*comment out FOR NOW REGIONAL*/}
        <SpotlightListCollectionRegional />

        {/*LATEST RELEASES*/}
        {displaySuppliersRecentlyAdded && (
          <DiscoveryV2RecentlyAdded
            products={discoveryRecentlyAddedQuery.data}
            onProductClicked={onProductClicked}
          />
        )}

        {/*PRE-ORDERS*/}
        {preOrdersExist && (
          <DiscoveryV2PreOrders
            products={preOrderQuery.data}
            onProductClicked={onProductClicked}
          />
        )}

        {/*PRODUCT STYLE SPOTLIGHTS*/}
        <SpotlightListCollectionProducts />

        {/*SUPPLIERS RECENTLY ADDED*/}
        <DiscoverySuppliersRecentlyAdded
          buyerPostcode={buyerPostcode}
          unAuthorisedUserPostcode={unAuthorisedUserPostcode}
        />

        {/*FIND BY CONTAINERS*/}
        <DiscoveryV2ContainersSection />

        {/*BEST SELLERS*/}
        {displayBestsellers && (
          <DiscoveryV2Bestsellers
            products={bestsellersProducts}
            onProductClicked={onProductClicked}
          />
        )}

        {business?.Retailer && showVenueTypesForm && (
          <DiscoveryV2BusinessDetails />
        )}

        <div className={"px-4 pb-4"}>
          {discoverySupplierListQuery.isLoading ? (
            <SpinnerLegacy />
          ) : (
            <>
              <div className="flex flex-col gap-4">
                <H5 className={twMerge("flex items-center")}>
                  Find more on Sellar
                </H5>
              </div>
              <div
                className={twMerge(
                  "grid grid-cols-1 gap-4 pt-4 md:grid-cols-2 lg:grid-cols-3",
                )}
              >
                {discoverySupplierListQuery.data?.pages.map((page, pageIndex) =>
                  page.map((supplier, index) => (
                    <>
                      <DiscoveryListSupplierCardV2
                        key={supplier.id}
                        supplier={supplier}
                        onClick={() => {
                          navigateToChild(
                            ORDER_BUYER.href({
                              supplierIdOrUrlId: supplier.urlId,
                            }),
                          );
                          track("Discover supplier clicked", {
                            supplier: supplier.urlId,
                            source: "More on Sellar",
                            sessionId,
                            ...getSellarWebsiteUtms(),
                          });
                        }}
                      />
                      {!business && (
                        <>
                          {index === 9 && pageIndex === 0 && (
                            <div className={"md:col-span-2"}>
                              <SaveTimeValueProp />
                            </div>
                          )}
                          {index === 5 && pageIndex === 1 && (
                            <div className={"md:col-span-2"}>
                              <BestPricesValueProp />
                            </div>
                          )}
                          {index === 5 && pageIndex === 2 && (
                            <div className={"md:col-span-2"}>
                              <NationwideValueProp />
                            </div>
                          )}
                        </>
                      )}
                    </>
                  )),
                )}
                <IntersectingInfiniteLoadingButton
                  query={discoverySupplierListQuery}
                />
              </div>
            </>
          )}
        </div>
      </div>
      <StorefrontPostcodeDialogContainer
        setPostcodeDialogActive={setPostcodeDialogActive}
        postcodeDialogActive={postcodeDialogActive}
        setUnAuthorisedUserPostcode={setUnAuthorisedUserPostcode}
        unAuthorisedUserPostcode={unAuthorisedUserPostcode}
        title={"Trade ordering from the UK's best craft drinks brands"}
        description={"Enter your venue's postcode to see what's available"}
      />
      <BestPricePromiseDialog
        productName={selectedProduct?.name}
        buyerName={selectedProduct?.name}
        supplierName={selectedProduct?.Supplier?.Business?.name}
        active={bestPricePromiseDialogActive}
        onDismiss={() => setBestPricePromiseDialogActive(false)}
      />

      {selectedProduct && (
        <DiscoveryProductDialog
          productId={selectedProduct.id}
          supplierId={selectedProduct.Supplier?.id}
          active={!!selectedProduct}
          onDismiss={() => {
            setSelectedProduct(undefined);
          }}
          setBestPricePromiseDialogActive={() => {
            setBestPricePromiseDialogActive(true);
          }}
        />
      )}
      {user && (
        <DraftBasketDrawer
          active={draftBasketDrawerActive}
          setActive={setDraftBasketDrawerActive}
          showDeliveredBySellarFeatures={
            sdConsolidatedCheckoutEnabled && canShowDeliveredBySellar
          }
        />
      )}
    </MarketBasketFeedbackProvider>
  );
};

const MatchedBusinessValueProp = () => {
  const { business } = useSellarCurrentBusiness();
  const [unAuthorisedUserPostcode] = useLocalStorage<string | undefined>(
    "unauthorisedUserPostcode",
    undefined,
  );
  const { setSignupDialogActive } = useSignupContext();
  const _matchedBusinessesQuery = useMatchedBuyersList(
    {
      query: [
        {
          type: "postalCode",
          value: unAuthorisedUserPostcode ?? "",
          modifier: "EQUALS",
        },
      ],
    },
    {
      enabled: !!unAuthorisedUserPostcode && !business,
    },
  );
  if (
    business ||
    !unAuthorisedUserPostcode
    // ||
    // matchedBusinessesQuery.isLoading ||
    // !matchedBusinessesQuery?.data?.pages?.[0]?.[0]
  )
    return;
  return (
    <div
      className={
        "mx-4 flex flex-col justify-between gap-4 rounded-lg border border-zinc-300 bg-zinc-50 p-4 lg:flex-row"
      }
    >
      <div className="flex flex-col justify-center gap-2 lg:flex-row lg:items-center">
        <div className="flex h-[32px] w-[32px] items-center justify-center rounded-full border border-[#B557F1] bg-purple-100">
          <Icon className={"h-[18px] w-[18px] text-[#B557F1]"} icon={"BELL"} />
        </div>
        <div className="flex flex-col">
          <p className={"text-sm font-semibold"}>
            We've found some business account matches for your postcode{" "}
            {unAuthorisedUserPostcode}
          </p>
          <p className={"text-sm"}>
            Your account may already be set up with us. Activate it now to see
            all trade pricing and order direct with the UK's best craft drinks
            providers
          </p>
        </div>
      </div>
      <Button size={"small"} onClick={() => setSignupDialogActive(true)}>
        Activate Account
      </Button>
    </div>
  );
};
const SaveTimeValueProp = () => {
  return (
    <ValuePropsContainer
      title={"Save time and reduce admin"}
      description={
        <span>
          <span className={"underline decoration-[#B557F1]"}>One account</span>{" "}
          for 100s of the UK's best craft drinks providers
        </span>
      }
      buttonText={"Get started"}
    />
  );
};
const BestPricesValueProp = () => {
  return (
    <ValuePropsContainer
      title={"Best prices guaranteed"}
      description={
        <span>
          When ordering on Sellar, you're buying{" "}
          <span className={"underline decoration-[#B557F1]"}>direct</span> from
          the supplier with no hidden fees
        </span>
      }
      buttonText={"See pricing"}
    />
  );
};

const NationwideValueProp = () => {
  return (
    <ValuePropsContainer
      title={"Nationwide delivery"}
      description={
        <span>
          Get orders delivered from{" "}
          <span className={"underline decoration-[#B557F1]"}>anywhere</span> in
          the UK
        </span>
      }
      buttonText={"Order now"}
    />
  );
};
const DiscoveryV2ContainersSection = () => {
  return (
    <div className="px-4 pt-4">
      <div className="flex flex-col gap-4">
        <H5 className={twMerge("flex items-center")}>
          Find products by container
        </H5>
        <DiscoveryContainerCards />
      </div>
    </div>
  );
};
const DiscoveryV2RecentlyViewed = ({
  products,
}: {
  products: DiscoveryV2RecentlyViewedStoreState["recentlyViewed"] | undefined;
}) => {
  const { track } = useEvents();
  const { business } = useSellarCurrentBusiness();
  const { sessionId } = useMarketStorefrontSessionId();
  const isMobile = useMediaQuery("(max-width: 700px)");
  const [selectedProduct, setSelectedProduct] = useState<
    DiscoveryV2RecentlyViewedStoreState["recentlyViewed"][number] | undefined
  >(undefined);

  const [bestPricePromiseDialogActive, setBestPricePromiseDialogActive] =
    useState<boolean>(false);

  const supplierQuery = useSupplier(selectedProduct?.supplierId, {
    enabled: !!selectedProduct?.supplierId,
  });
  const hasProductResults = !!products?.length;
  if (!hasProductResults) return null;

  const unexpiredProducts = products.filter((product) =>
    notRecentlyViewedExpired(product.expiry),
  );
  const onProductSelected = (
    selectedProduct: DiscoveryV2RecentlyViewedStoreState["recentlyViewed"][number],
  ) => {
    setSelectedProduct(selectedProduct);
    track("Discover product clicked", {
      supplier: selectedProduct.urlId,
      product: selectedProduct.name,
      source: "Recently Viewed",
      containsPreOrderProductItem: selectedProduct.containsPreOrderProductItem,
      sessionId,
      ...getSellarWebsiteUtms(),
    });
  };

  if (unexpiredProducts.length === 0) return null;

  return (
    <>
      <H5>Recently Viewed</H5>
      <div
        className={twMerge(
          "[scrollbar-width:none] overflow-x-scroll [::-webkit-scrollbar]:hidden",
          !!selectedProduct && isMobile && "fixed",
        )}
      >
        <div className={"flex w-fit gap-2"}>
          {unexpiredProducts.map((product, i) => (
            <DiscoveryV2RecentlyViewedProduct
              product={product}
              key={i}
              onProductSelected={onProductSelected}
            />
          ))}
        </div>
      </div>
      <DiscoveryProductDialog
        productId={selectedProduct?.id}
        supplierId={selectedProduct?.supplierId}
        active={!!selectedProduct}
        onDismiss={() => {
          setSelectedProduct(undefined);
        }}
        setBestPricePromiseDialogActive={() => {
          setBestPricePromiseDialogActive(true);
        }}
      />
      {supplierQuery.data && (
        <BestPricePromiseDialog
          active={bestPricePromiseDialogActive}
          productName={selectedProduct?.name ?? ""}
          buyerName={business?.name}
          supplierName={supplierQuery.data.Business.name}
          onDismiss={() => setBestPricePromiseDialogActive(false)}
        />
      )}
    </>
  );
};

const DiscoveryV2RecentlyViewedProduct = ({
  product,
  onProductSelected,
}: {
  product: DiscoveryV2RecentlyViewedStoreState["recentlyViewed"][number];
  onProductSelected: (
    selectedProduct: DiscoveryV2RecentlyViewedStoreState["recentlyViewed"][number],
  ) => void;
}) => {
  return (
    <div
      className="flex max-w-[300px] shrink-0 items-center rounded-lg bg-white p-2"
      onClick={() => onProductSelected(product)}
    >
      <figure className="relative mr-2 shrink-0">
        <ImageWithFallback
          img={product.imageUrl}
          fallback="product-badge"
          className="h-12 w-12 rounded-full object-cover"
        />
        <ImageWithFallback
          img={product.logoUrl}
          fallback="supplier-badge"
          className="absolute -right-1 bottom-0 h-4 w-4 rounded-full object-cover"
        />
      </figure>
      <div className="mx-2 min-w-0 flex-1">
        <H5 className="overflow-hidden font-semibold text-nowrap text-ellipsis">
          {product.name}
        </H5>
        <H6 className="overflow-hidden text-sm text-nowrap text-ellipsis text-gray-500">
          {product.details}
        </H6>
      </div>
      <Icon icon="CHEVRON_RIGHT" className="shrink-0 text-zinc-400" />
    </div>
  );
};

const DiscoveryV2SuppliersLocal = () => {
  const { business } = useSellarCurrentBusiness();
  const navigate = useNavigate();

  const [unAuthorisedUserPostcode] = useLocalStorage<string | undefined>(
    "unauthorisedUserPostcode",
    undefined,
  );
  const [unAuthorisedUserLocation] = useLocalStorage<
    { lat: number; lng: number } | undefined
  >("unAuthorisedUserLocation", undefined);

  const buyerPostcode =
    business?.type === "retailer"
      ? business.DeliveryAddress.postalCode
      : undefined;

  const suppliersByDistanceQuery = useSupplierListByDistance({
    offset: 0,
    limit: 15,
    lng: business?.Location?.lng ?? unAuthorisedUserLocation?.lng ?? 0,
    lat: business?.Location?.lat ?? unAuthorisedUserLocation?.lat ?? 0,
    distance: 150,
    retailerPostcode: buyerPostcode,
    unAuthorisedUserPostcode,
    orderBy: "distance",
  });

  const suppliersByDistance =
    suppliersByDistanceQuery.data?.pages.flatMap((page) => page) ?? [];

  const { add } = useSellarMapStore(
    useShallow((state) => ({
      add: state.add,
    })),
  );

  add(business?.Location ?? defaultLondonLocation);

  const onClick = () => {
    useSellarMapStore.getState().reset();
    navigate(DISCOVER_SUPPLIER_MAP);
  };

  return (
    <div className="px-4 pt-4">
      <div className="flex flex-col gap-4">
        <H5 className={"flex cursor-pointer items-center"} onClick={onClick}>
          See availability from suppliers local to you{" "}
          <Icon icon={"CHEVRON_RIGHT"} />
        </H5>
        <div className="cursor-pointer" onClick={onClick}>
          <div className="pointer-events-none h-[300px] overflow-hidden rounded-lg border border-zinc-300">
            <SellarMap
              entities={suppliersByDistance}
              noClustering={true}
              displaying="supplier"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

const DiscoveryV2BusinessDetails = () => {
  const [isActive, setIsActive] = useState<boolean>(true);
  return (
    <>
      {isActive && (
        <Card className="bg-white">
          <CardContent className={"p-8"}>
            <div className="flex cursor-pointer justify-end">
              <Icon icon={"CLOSE"} onClick={() => setIsActive(false)} />
            </div>
            <BuyerVenueTypeForm />
          </CardContent>
        </Card>
      )}
    </>
  );
};
const DiscoveryV2RecentlyAdded = ({
  products,
  onProductClicked,
}: {
  products:
    | InfiniteData<DiscoveryProductsRecentlyAddedResponseType>
    | undefined;
  onProductClicked: (
    product: DiscoveryProductsRecentlyAddedResponseType[number],
    source: string,
  ) => void;
}) => {
  const navigate = useNavigate();
  const hasProductResults = !!products?.pages[0]?.length;

  if (!hasProductResults) return null;

  return (
    <DiscoveryHorizontalList
      title="Shop the latest releases"
      onClick={() => navigate(DISCOVER_RECENTLY_ADDED)}
    >
      {products.pages[0]?.map((product) => (
        <div
          key={product.id}
          className={twMerge("w-[200px] min-w-[200px] flex-none")}
        >
          <DiscoveryListProductCard
            product={product}
            onClick={() => onProductClicked(product, "Recently Added")}
          />
        </div>
      ))}
    </DiscoveryHorizontalList>
  );
};

const DiscoveryV2PreOrders = ({
  products,
  onProductClicked,
}: {
  products: InfiniteData<DiscoveryPreOrdersResponseType> | undefined;
  onProductClicked: (
    product: DiscoverPreOrderProductWithSupplierType,
    source: string,
  ) => void;
}) => {
  const navigate = useNavigate();
  const hasProductResults = !!products?.pages[0]?.length;

  if (!hasProductResults) return null;

  return (
    <div className="relative">
      <DiscoveryHorizontalList
        title="Pre-order releases"
        onClick={() => navigate(DISCOVER_PRE_ORDER)}
      >
        {products.pages[0]?.map((product) => (
          <div
            key={product.id}
            className={twMerge("w-[200px] min-w-[200px] flex-none")}
          >
            <DiscoveryListProductCard
              product={product}
              onClick={() => onProductClicked(product, "Pre-order")}
            />
          </div>
        ))}
      </DiscoveryHorizontalList>
      <PreOrderPillToolTip />
    </div>
  );
};

const DiscoveryV2HomeMenu = ({
  onChangeSearchValue,
}: {
  onChangeSearchValue?: (searchValue: string) => void;
}) => {
  const [searchSuggestions, setSearchSuggestions] = useState(
    randomizeDiscoverySuggestionTags(),
  );

  const { recentlyViewed } = useDiscoveryV2RecentlyViewedStore(
    useShallow((state) => ({
      recentlyViewed: state.recentlyViewed,
    })),
  );

  const { searchHistory, reset } = useDiscoveryV2SearchHistoryStore(
    useShallow((state) => ({
      searchHistory: state.searchHistory,
      reset: state.reset,
    })),
  );

  const { isDisplaySizeSatisfied, target } = useDisplaySizeVariant();

  const is4Xl = isDisplaySizeSatisfied("4xl");

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto overscroll-contain pb-4">
      <DiscoveryV2RecentlyViewed products={recentlyViewed} />
      <div
        ref={target}
        className={twMerge(
          "grid items-start gap-4 divide-zinc-200",
          is4Xl && "grid-cols-3",
        )}
      >
        <DiscoveryContainerCards />
        <div className={"flex flex-col gap-2"}>
          <div className={"flex items-center justify-between"}>
            <H5>Search suggestions</H5>
            <Button
              size={"small"}
              color={"transparent"}
              onClick={() =>
                setSearchSuggestions(randomizeDiscoverySuggestionTags())
              }
            >
              Refresh
            </Button>
          </div>
          <DiscoverySuggestionTagContainer>
            {searchSuggestions.map((tag) => (
              <DiscoverySuggestionTag
                key={tag}
                tag={tag}
                onClick={() => onChangeSearchValue?.(tag)}
              />
            ))}
          </DiscoverySuggestionTagContainer>
        </div>
        <div className={"flex flex-col gap-2"}>
          <div className={"flex items-center justify-between"}>
            <H5>Previous searches</H5>
            <Button
              size={"small"}
              color={"transparent"}
              onClick={() => reset()}
            >
              Clear all
            </Button>
          </div>
          <div className={"flex flex-col gap-2"}>
            {searchHistory.map((searchValue, i) => (
              <div
                key={i}
                className={"flex cursor-pointer items-center gap-2"}
                onClick={() => onChangeSearchValue?.(searchValue)}
              >
                <Icon icon={"HISTORY"} />
                {searchValue}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

const DiscoveryV2Bestsellers = ({
  products,
  onProductClicked,
}: {
  products: DiscoveryProductsBestsellersListResponseType;
  onProductClicked: (
    product: DiscoveryProductsRecentlyAddedResponseType[number],
    source: string,
  ) => void;
}) => {
  const navigate = useNavigate();

  return (
    <DiscoveryHorizontalList
      title="Shop the bestsellers"
      onClick={() => navigate(DISCOVER_BEST_SELLERS)}
    >
      {products.map((product) => (
        <div
          key={product.id}
          className={twMerge("w-[200px] min-w-[200px] flex-none")}
        >
          <DiscoveryListProductCard
            product={product}
            onClick={() => onProductClicked(product, "Bestsellers")}
          />
        </div>
      ))}
    </DiscoveryHorizontalList>
  );
};

const DiscoverySuppliersRecentlyAdded = ({
  buyerPostcode,
  unAuthorisedUserPostcode,
}: {
  buyerPostcode?: string;
  unAuthorisedUserPostcode?: string;
}) => {
  const navigate = useNavigate();
  const navigateToChild = useNavigateToChild();
  const recentlyAddedSuppliersQuery = useSupplierRecentlyAddedNew({
    retailerPostcode: buyerPostcode,
    unAuthorisedUserPostcode,
  });
  const suppliers =
    recentlyAddedSuppliersQuery.data?.pages.flatMap((page) => page) ?? [];

  const { isLoading } = recentlyAddedSuppliersQuery;
  if (isLoading || suppliers.length === 0) return <></>;
  return (
    <DiscoveryHorizontalList
      title="Discover suppliers recently added to Sellar"
      onClick={() => navigate(DISCOVER_SUPPLIERS_RECENT)}
    >
      {suppliers.map((supplier, idx) => (
        <div key={idx} className={twMerge("w-[200px] min-w-[200px] flex-none")}>
          <DiscoverySupplierCardCompact
            supplier={{ ...supplier, StorefrontAvailability: null }}
            onClick={() =>
              navigateToChild(
                ORDER_BUYER.href({ supplierIdOrUrlId: supplier.urlId }),
              )
            }
            className={twMerge("w-[200px] flex-none")}
          />
        </div>
      ))}
    </DiscoveryHorizontalList>
  );
};

const Spotlight = () => {
  const marketSpotlightPageLayoutQuery = useMarketSpotlightPageLayout({
    type: "featured",
  });

  const pageIds = marketSpotlightPageLayoutQuery.data?.pageIds;
  const pageId = pageIds?.[0];
  if (!pageId) return null;
  return <SpotlightBanner pageId={pageId} />;
};

const DiscoveryV2DirectSuppliers = ({
  buyerPostcode,
  unAuthorisedUserPostcode,
  unAuthorisedUserLocation,
}: {
  buyerPostcode?: string;
  unAuthorisedUserPostcode?: string;
  unAuthorisedUserLocation?: { lat: number; lng: number };
}) => {
  const navigate = useNavigate();
  const navigateToChild = useNavigateToChild();
  const { business } = useSellarCurrentBusiness();
  const directSuppliersQuery = useSupplierListByDistance({
    offset: 0,
    limit: 15,
    lng:
      business?.Location?.lng ??
      unAuthorisedUserLocation?.lng ??
      defaultLondonLocation.lng,
    lat:
      business?.Location?.lat ??
      unAuthorisedUserLocation?.lat ??
      defaultLondonLocation.lat,
    distance: 150,
    retailerPostcode: buyerPostcode,
    unAuthorisedUserPostcode,
    orderBy: "distance",
    directDelivery: true,
  });

  const suppliers =
    directSuppliersQuery.data?.pages.flatMap((page) => page) ?? [];

  const postcode =
    business?.DeliveryAddress.postalCode.split(" ")[0] ??
    unAuthorisedUserPostcode?.split(" ")[0] ??
    undefined;

  const { isLoading, isFetched } = directSuppliersQuery;
  if (isLoading) return <SpinnerLegacy />;
  if (isFetched && suppliers.length === 0) return <></>;
  return (
    <DiscoveryHorizontalList
      title={`Suppliers delivering direct${postcode ? ` to ${postcode}` : ""}`}
      onClick={() =>
        navigate(DISCOVER_SUPPLIERS_DIRECT, {
          state: { unAuthorisedUserLocation, unAuthorisedUserPostcode },
        })
      }
    >
      {suppliers.map((supplier, idx) => (
        <div key={idx} className={twMerge("w-[200px] flex-none")}>
          <DiscoverySupplierCardCompact
            supplier={{ ...supplier, StorefrontAvailability: null }}
            onClick={() =>
              navigateToChild(
                ORDER_BUYER.href({ supplierIdOrUrlId: supplier.urlId }),
              )
            }
            className={twMerge("w-[200px] flex-none")}
            footer={
              supplier.Business.milesFromMe ? (
                <>
                  <Icon icon="MAP_MARKER" />
                  {supplier.Business.milesFromMe.toFixed(0)}{" "}
                  {pluralize("mile", supplier.Business.milesFromMe)} away
                </>
              ) : undefined
            }
          />
        </div>
      ))}
    </DiscoveryHorizontalList>
  );
};
